import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Countdown } from './Countdown';
import { Podium } from './Podium';
import { formatDateRange, formatMoney } from '../format';
import type { Race } from '../api';

const phases = {
  running: 'Yarışma devam ediyor',
  upcoming: 'Yakında başlıyor',
  awaiting_results: 'Sonuçlar doğrulanıyor',
  finished: 'Yarışma tamamlandı',
};

/** Race facts and podium share one composition, rather than separate unrelated panels. */
export function RaceShowcase({
  race,
  detail = false,
  headingLevel = 'h3',
  action,
  children,
}: {
  race: Race;
  detail?: boolean;
  headingLevel?: 'h2' | 'h3';
  action?: ReactNode;
  children?: ReactNode;
}) {
  const isOver = race.phase === 'finished' || race.phase === 'awaiting_results';
  const Heading = detail ? 'h1' : headingLevel;
  return (
    <div className={cn('race-showcase', detail && 'race-showcase-detail')}>
      <div className="race-showcase-top">
        <div className="race-partner">
          {race.siteIconUrl ? <img src={race.siteIconUrl} alt="" width={32} height={32} /> : null}
          <span>{race.siteName}</span>
          <span className="race-partner-separator" />
          <span className="text-surface-400">Wager Race</span>
        </div>
        <span className={cn('race-phase', race.phase === 'running' && 'race-phase-running')}>
          <span aria-hidden="true" />
          {phases[race.phase]}
        </span>
      </div>
      <div className="race-showcase-body">
        <div className="race-showcase-info">
          <Heading className="race-showcase-title">{race.title}</Heading>
          <p className="race-dates">{formatDateRange(race.startsAtUtc, race.endsAtUtc)}</p>
          <div className="race-pool">
            <p>Toplam ödül havuzu</p>
            <strong>{formatMoney(race.prizePool, race.currency)}</strong>
          </div>
          <div className="race-clock">
            <p>
              {isOver
                ? 'Yarışma durumu'
                : race.phase === 'upcoming'
                  ? 'Başlamasına kalan süre'
                  : 'Bitmesine kalan süre'}
            </p>
            {isOver ? (
              <span className="text-sm text-surface-300">{phases[race.phase]}</span>
            ) : (
              <Countdown
                targetIso={race.phase === 'upcoming' ? race.startsAtUtc : race.endsAtUtc}
                overLabel="Sona erdi"
              />
            )}
          </div>
          {action}
        </div>
        <div className="race-showcase-stage">
          <div className="race-stage-label">
            <span />
            {race.phase === 'finished' ? 'Kazananlar' : 'Zirvedeki oyuncular'}
            <span />
          </div>
          {race.podium.length ? (
            <Podium rows={race.podium} currency={race.currency} />
          ) : (
            <div className="race-stage-empty">
              <span aria-hidden="true">◇</span>
              <p>
                {race.phase === 'upcoming'
                  ? 'Yarışma henüz başlamadı.'
                  : 'Sıralama henüz yüklenmedi.'}
              </p>
            </div>
          )}
        </div>
      </div>
      {children ? <div className="race-showcase-bottom">{children}</div> : null}
    </div>
  );
}
